import React, { useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { Formik } from 'formik';
import { TextField, Button, Link } from '@material-ui/core';
import * as Yup from 'yup';

import * as Styled from './Login.styled';
import logo from '../assets/logo';
import { usePasswordReset } from '../user/User.hooks';
import { useRemoveJiraWidget } from '../common/useRemoveJiraWidget';

const passwordMinLength = 8;
const passwordRegex: RegExp = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/;
const passwordRegexErrorMessage =
  'Need at least an uppercase and a lowercase letter, and a number.';
const invalidTokenError =
  'Password reset token is missing, invalid or has expired.';
interface PasswordResetForm {
  password: string;
  passwordConfirmation: string;
}

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required.')
    .matches(passwordRegex, passwordRegexErrorMessage)
    .min(passwordMinLength, `Minimum ${passwordMinLength} characters.`),
  passwordConfirmation: Yup.string()
    .required('Password confirmation is required.')
    .oneOf([Yup.ref('password'), null], 'Passwords must match.')
});

const hasErrors = (values: PasswordResetForm): boolean => {
  try {
    validationSchema.validateSync(values);
    return false;
  } catch (_) {
    return true;
  }
};

type PasswordResetQueryParams = {
  token?: string;
};

export type PasswordResetProps = RouteComponentProps<PasswordResetQueryParams>;

const PasswordReset: React.FC<PasswordResetProps> = (
  props: PasswordResetProps
) => {
  useRemoveJiraWidget();
  const { location } = props;
  const {
    user,
    success,
    isLoading,
    error,
    verifyError,
    doPasswordReset,
    doVerifyResetToken
  } = usePasswordReset();
  const tokenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // get token from query
    const params = new URLSearchParams(location.search);
    const token: string | null = params.get('token');
    tokenRef.current = token || undefined;
    doVerifyResetToken(token || '');
  }, [doVerifyResetToken, location.key, location.search]);

  let errorMessage: string | undefined = undefined;
  if (error) errorMessage = 'Unable to reset password.';
  else if (verifyError) errorMessage = invalidTokenError;

  const handleSubmit = (form: PasswordResetForm) => {
    const token = tokenRef.current;
    errorMessage = token ? undefined : 'No reset token found.';
    token && doPasswordReset(form.password, token);
  };

  let title: string = 'Verifying reset token...';
  if (user) title = 'Password Reset';
  else if (verifyError) title = 'Invalid token';

  return (
    <Styled.StyledLoginContainer>
      <Styled.StyledLoginCard>
        <img src={logo} alt='Sierra Labs' className='logo' />
        <h2 style={!user || success ? { marginBottom: '18px' } : {}}>
          {title}
        </h2>

        {user && !success && (
          <Formik
            initialValues={
              {
                password: '',
                passwordConfirmation: ''
              } as PasswordResetForm
            }
            validationSchema={validationSchema}
            validateOnBlur={true}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <Styled.StyledLoginForm>
                <TextField
                  variant='outlined'
                  type='password'
                  name='password'
                  label='New Password'
                  className='form-field'
                  placeholder='Enter your new password'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading}
                  value={values.password}
                  error={touched.password && !!errors.password}
                  helperText={errors.password}
                />

                <TextField
                  variant='outlined'
                  type='password'
                  name='passwordConfirmation'
                  label='Confirm Password'
                  className='form-field'
                  placeholder='Re-enter password'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading}
                  value={values.passwordConfirmation}
                  error={
                    touched.passwordConfirmation &&
                    !!errors.passwordConfirmation
                  }
                  helperText={errors.passwordConfirmation}
                />

                {!isLoading && error && <p className='error'>{errorMessage}</p>}

                <Button
                  type='submit'
                  variant='contained'
                  disableElevation
                  disabled={isLoading || hasErrors(values)}
                >
                  Submit
                </Button>
              </Styled.StyledLoginForm>
            )}
          </Formik>
        )}

        {!isLoading && verifyError && <p className='error'>{errorMessage}</p>}

        {success && (
          <Styled.StyledLoginCardContent>
            <div>Password reset successful!</div>
            <div>You can now proceed to the login page to sign in.</div>
          </Styled.StyledLoginCardContent>
        )}

        <Link className='forgot-password-link' href='/login'>
          {success ? 'Proceed to Login' : 'Back to Login'}
        </Link>
      </Styled.StyledLoginCard>
    </Styled.StyledLoginContainer>
  );
};

export default withRouter(PasswordReset);
